import React, { useEffect, useRef, useState } from 'react';

function cameraErrorMessage(err) {
  const name = String(err?.name || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'Camera access was denied. Allow Camera in iPhone Settings and try again.';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'No usable camera was found on this device.';
  return err?.message || 'Unable to access the camera.';
}

function validHttpUrl(value) {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'https:' ? url.toString() : '';
  } catch (_) {
    return '';
  }
}

function waitForVideo(ref) {
  return new Promise(resolve => {
    const check = () => {
      if (ref.current) resolve(ref.current);
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

export default function IapQrScanner({ onScan, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const completedRef = useRef(false);
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [engine, setEngine] = useState('');
  const [open, setOpen] = useState(true);

  const stop = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    if (zxingControlsRef.current) {
      try { zxingControlsRef.current.stop(); } catch (_) {}
      zxingControlsRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const finish = value => {
    if (completedRef.current) return;
    const url = validHttpUrl(value);
    if (!url) {
      setError('That QR does not contain a secure HTTPS web link.');
      return;
    }
    completedRef.current = true;
    stop();
    setOpen(false);
    onScan?.(url);
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera scanning is not available here. Paste the IAP QR link below instead.');
        return;
      }
      try {
        const video = await waitForVideo(videoRef);
        if (cancelled) return;

        if ('BarcodeDetector' in window) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
          streamRef.current = stream;
          video.srcObject = stream;
          await video.play();
          const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          setEngine('native');
          const scanFrame = async () => {
            try {
              if (videoRef.current?.readyState >= 2) {
                const codes = await detector.detect(videoRef.current);
                if (codes?.[0]?.rawValue) finish(codes[0].rawValue);
              }
            } catch (_) {}
            if (!completedRef.current) frameRef.current = requestAnimationFrame(scanFrame);
          };
          frameRef.current = requestAnimationFrame(scanFrame);
        } else {
          const { BrowserQRCodeReader } = await import('@zxing/browser');
          const reader = new BrowserQRCodeReader();
          setEngine('zxing');
          zxingControlsRef.current = await reader.decodeFromConstraints(
            { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
            video,
            result => {
              const value = result?.getText?.() || result?.text || '';
              if (value) finish(value);
            },
          );
        }
      } catch (err) {
        if (!cancelled) setError(cameraErrorMessage(err));
      }
    };

    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  if (!open) return null;

  return (
    <div className="iap-scan-panel">
      <div className="iap-scan-head">
        <div>
          <span className="iap-kicker">PHYSICAL IAP QR</span>
          <h3>Scan the incident IAP QR</h3>
          <p>Line Medic will open the source so you can click through to the actual IAP PDF you want to save.</p>
        </div>
        <button type="button" className="iap-secondary-btn" onClick={() => { stop(); onCancel?.(); }}>Cancel</button>
      </div>

      {!error && <video ref={videoRef} className="iap-scanner-video" playsInline muted />}
      {engine === 'zxing' && !error && <small className="iap-muted">iPhone-compatible QR decoder active</small>}
      {error && <div className="iap-alert">{error}</div>}

      <div className="iap-manual-row">
        <input
          type="url"
          value={manual}
          onChange={event => setManual(event.target.value)}
          placeholder="Or paste the IAP QR/source link"
          autoCapitalize="none"
          autoCorrect="off"
        />
        <button type="button" className="iap-primary-btn" disabled={!manual.trim()} onClick={() => finish(manual)}>Continue</button>
      </div>
    </div>
  );
}
